import React, { createContext, useContext, useEffect, useMemo, useState } from "react"
import { usePanorama } from "./PanoramaContext"

// Labels are functions: they are read on render, once I18n knows the locale.
const FAMILIES = [
  {
    id: "chantier",
    label: () => I18n.t("projects.tabs.families.chantier"),
    sections: [
      { id: "summary",   label: () => I18n.t("projects.tabs.sections.summary") },
      { id: "wbs",       label: () => I18n.t("projects.tabs.sections.wbs") },
      { id: "gantt",     label: () => I18n.t("projects.tabs.sections.gantt") },
      { id: "reserves",  label: () => I18n.t("projects.tabs.sections.reserves") },
      { id: "equipments", label: () => I18n.t("projects.tabs.sections.equipments") },
      { id: "library",   label: () => I18n.t("projects.tabs.sections.library") },
      { id: "documents", label: () => I18n.t("projects.tabs.sections.documents") },
    ],
  },
  {
    id: "achats",
    label: () => I18n.t("projects.tabs.families.achats"),
    sections: [
      { id: "engagements",  label: () => I18n.t("projects.tabs.sections.engagements") },
      { id: "subcontracts", label: () => I18n.t("projects.tabs.sections.subcontracts") },
      { id: "expenses",     label: () => I18n.t("projects.tabs.sections.expenses") },
    ],
  },
  {
    id: "facturation",
    label: () => I18n.t("projects.tabs.families.facturation"),
    sections: [
      { id: "milestones", label: () => I18n.t("projects.tabs.sections.milestones") },
      { id: "avenants",   label: () => I18n.t("projects.tabs.sections.avenants") },
      { id: "payments",   label: () => I18n.t("projects.tabs.sections.payments") },
    ],
  },
  {
    id: "rentabilite",
    label: () => I18n.t("projects.tabs.families.rentabilite"),
    sections: [
      { id: "budgets",      label: () => I18n.t("projects.tabs.sections.budgets") },
      { id: "margins",      label: () => I18n.t("projects.tabs.sections.margins") },
      { id: "valorisation", label: () => I18n.t("projects.tabs.sections.valorisation"), basicOnly: true },
      { id: "analytics",    label: () => I18n.t("projects.tabs.sections.analytics"),   advancedOnly: true },
      { id: "finances",     label: () => I18n.t("projects.tabs.sections.finances") },
    ],
  },
  {
    id: "equipe",
    label: () => I18n.t("projects.tabs.families.equipe"),
    sections: [
      { id: "hours",         label: () => I18n.t("projects.tabs.sections.hours") },
      { id: "interventions", label: () => I18n.t("projects.tabs.sections.interventions") },
    ],
  },
]

const treeCount = nodes =>
  (nodes || []).reduce((total, node) => total + 1 + treeCount(node.children), 0)

const countOf = (id, state) => {
  switch (id) {
    case "wbs":           return treeCount(state.tasks)
    case "reserves":      return state.reserves?.items?.length ?? null
    case "interventions": return state.interventions?.length ?? null
    case "engagements":   return state.engagements?.count ?? state.engagements?.items?.length ?? null
    case "subcontracts":  return state.subcontracts?.items?.length ?? null
    case "expenses":      return state.expenses?.count ?? state.expenses?.items?.length ?? null
    case "library":       return state.library?.count ?? state.library?.items?.length ?? null
    case "avenants":      return state.avenants?.items?.length ?? null
    case "payments":      return state.payments?.rows?.length ?? null
    case "hours":         return state.hours?.workers?.length ?? null
    case "equipments":    return state.equipments?.totals?.installed ?? null
    case "documents": {
      const d = state.documents || {}
      const total = ["quotes", "invoices", "purchase_orders", "purchase_invoices"]
        .reduce((sum, key) => sum + (d[key]?.length || 0), 0)
      return total
    }
    default: return null
  }
}

const readInitial = known => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("tab")
    if (fromUrl && known.includes(fromUrl)) return fromUrl
  } catch (_) {}
  return known[0]
}

const TabsContext = createContext(null)

export const TabsProvider = ({ children }) => {
  const { state } = usePanorama()
  const advanced = !!state?.analytic_advanced

  const families = useMemo(
    () =>
      FAMILIES.map(family => ({
        ...family,
        label: family.label(),
        sections: family.sections
          .filter(section =>
            (!section.advancedOnly || advanced) && (!section.basicOnly || !advanced)
          )
          .map(section => ({ ...section, label: section.label(), count: countOf(section.id, state) })),
      })).filter(family => family.sections.length > 0),
    [state, advanced]
  )

  const sectionIds = useMemo(
    () => families.flatMap(family => family.sections.map(section => section.id)),
    [families]
  )

  const [current, setCurrent] = useState(() => readInitial(sectionIds))

  useEffect(() => {
    if (!sectionIds.includes(current)) setCurrent(sectionIds[0])
  }, [sectionIds, current])

  useEffect(() => {
    try {
      const url = new URL(window.location.href)
      url.searchParams.set("tab", current)
      window.history.replaceState({}, "", url)
    } catch (_) {}
  }, [current])

  const family = useMemo(
    () => families.find(f => f.sections.some(s => s.id === current)) || families[0],
    [families, current]
  )

  const goTo = id => {
    if (!sectionIds.includes(id)) return
    setCurrent(id)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <TabsContext.Provider value={{ families, family, current, setCurrent, goTo }}>
      {children}
    </TabsContext.Provider>
  )
}

export const useTabs = () => {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error("useTabs must be used within TabsProvider")
  return ctx
}
